import { supabase } from '@/integrations/supabase/client';
import type {
  PlatformFeeHistoryEntry,
  PlatformFeeOwnersPage,
  PlatformFeeOwnersQuery,
  PlatformFeeRateInfo,
  PlatformFeeSetResult,
} from '@/integrations/supabase/rpc-types';

/**
 * Aliquota della fee Place2Be. Il valore lo decide sempre il database
 * (resolve_platform_fee_rate): qui non c'e' nessuna costante, e nessuna deve tornarci.
 * Il cliente non chiama mai queste RPC, che lo respingono.
 */
export class PlatformFeeService {
  /** Aliquota di chi chiama: agenzia, membro (quella dell'agenzia) o indipendente. */
  static async getMyRate(): Promise<PlatformFeeRateInfo> {
    const { data, error } = await supabase.rpc('get_my_platform_fee_rate' as any);
    if (error) {
      throw new Error(`Errore nel caricamento dell'aliquota: ${error.message}`);
    }
    return data as unknown as PlatformFeeRateInfo;
  }

  /** Aliquota di un viaggio, per chi lo gestisce: congelata o quella del titolare. */
  static async getItineraryRate(itineraryId: string): Promise<PlatformFeeRateInfo> {
    const { data, error } = await supabase.rpc('get_itinerary_platform_fee_rate' as any, {
      p_itinerary_id: itineraryId,
    });
    if (error) {
      throw new Error(`Errore nel caricamento dell'aliquota del viaggio: ${error.message}`);
    }
    return data as unknown as PlatformFeeRateInfo;
  }

  /** Solo admin: agenzie o indipendenti con la loro aliquota, paginati. */
  static async getOwners({
    kind,
    search,
    onlyCustom = false,
    limit,
    offset,
  }: PlatformFeeOwnersQuery): Promise<PlatformFeeOwnersPage> {
    const { data, error } = await supabase.rpc('admin_get_platform_fee_owners' as any, {
      p_kind: kind,
      p_search: search?.trim() ? search.trim() : null,
      p_only_custom: onlyCustom,
      p_limit: limit,
      p_offset: offset,
    });
    if (error) {
      throw new Error(`Errore nel caricamento delle aliquote: ${error.message}`);
    }
    return data as unknown as PlatformFeeOwnersPage;
  }

  /** Solo admin: storico delle modifiche, dalla piu' recente. */
  static async getHistory(ownerId: string): Promise<PlatformFeeHistoryEntry[]> {
    const { data, error } = await supabase.rpc('admin_get_platform_fee_history' as any, {
      p_owner_id: ownerId,
    });
    if (error) {
      throw new Error(`Errore nel caricamento dello storico: ${error.message}`);
    }
    return (data ?? []) as unknown as PlatformFeeHistoryEntry[];
  }

  /**
   * Solo admin. `rate` come frazione; `null` riporta al default. La nota e' obbligatoria:
   * la RPC rifiuta il salvataggio senza.
   */
  static async setRate(ownerId: string, rate: number | null, note: string): Promise<PlatformFeeSetResult> {
    const { data, error } = await supabase.rpc('admin_set_platform_fee_rate' as any, {
      p_owner_id: ownerId,
      p_rate: rate,
      p_note: note,
    });
    if (error) {
      throw new Error(error.message);
    }
    return data as unknown as PlatformFeeSetResult;
  }
}
